import { useState } from "react";
import MuiAutocomplete, {
  type AutocompleteProps,
} from "@mui/material/Autocomplete";
import { useGoogleText } from "@/lib/translation/useGoogleText";

/** Keep option identities, filtering and callbacks in their original language.
 * Only the selected input's presentation comes from the shared Google widget.
 * In particular, a query or free-text draft must never be translated. */
export default function GoogleAutocomplete<
  Value,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false,
>(props: AutocompleteProps<Value, Multiple, DisableClearable, FreeSolo>) {
  const [uncontrolledValue, setUncontrolledValue] = useState(
    props.defaultValue,
  );
  const [editing, setEditing] = useState(false);
  const value = props.value === undefined ? uncontrolledValue : props.value;
  const getLabel =
    props.getOptionLabel ??
    ((option: unknown) =>
      typeof option === "string"
        ? option
        : String((option as { label?: string })?.label ?? ""));
  const singleValue =
    !props.multiple && value != null
      ? (value as Parameters<typeof getLabel>[0])
      : null;
  const source = singleValue == null ? "" : getLabel(singleValue);
  const isKnownOption =
    singleValue != null &&
    (!props.freeSolo ||
      props.options.some((option) =>
        props.isOptionEqualToValue
          ? props.isOptionEqualToValue(option, singleValue as Value)
          : option === singleValue,
      ));
  const translate = useGoogleText([
    ...(isKnownOption && source ? [source] : []),
    props.clearText ?? "Clear",
    props.closeText ?? "Close",
    props.openText ?? "Open",
  ]);

  return (
    <MuiAutocomplete
      {...props}
      clearText={translate(props.clearText ?? "Clear")}
      closeText={translate(props.closeText ?? "Close")}
      openText={translate(props.openText ?? "Open")}
      onChange={(event, next, reason, details) => {
        setUncontrolledValue(next);
        setEditing(false);
        props.onChange?.(event, next, reason, details);
      }}
      onInputChange={(event, input, reason) => {
        setEditing(reason === "input");
        props.onInputChange?.(event, input, reason);
      }}
      renderInput={(params) => {
        const input = params.slotProps.htmlInput;
        const showTranslation =
          !editing && isKnownOption && source && input.value === source;
        return props.renderInput({
          ...params,
          slotProps: {
            ...params.slotProps,
            htmlInput: {
              ...input,
              ...(showTranslation ? { value: translate(source) } : {}),
            },
          },
        });
      }}
    />
  );
}
